import type { NewsSource, NewsSourcePath } from '../../../api/DigiobcanApi';

/** Hodnota poľa je cesta ako JSON - `Autocomplete` pracuje s reťazcami. */
export const encodePath = (path: NewsSourcePath) => JSON.stringify(path);
export const decodePath = (value: string) =>
  JSON.parse(value) as NewsSourcePath;

export const formatSourcePath = (path: NewsSourcePath) => path.join(' / ');

/**
 * Ponuka vzorov: každý **prefix** každej zdrojovej cesty so súčtom noviniek
 * pod ním. Bez prefixov by sa nedalo vybrať `Úradná tabuľa`, lebo žiadna
 * novinka nemá práve takúto jednoúrovňovú cestu - všetky majú aj druh
 * dokumentu. Vzor totiž zahŕňa aj všetko pod sebou (viď API).
 */
export const sourceOptions = (
  sources: NewsSource[],
  extra: NewsSourcePath[] = [],
) => {
  const counts = new Map<string, { path: NewsSourcePath; count: number }>();
  for (const { path, news_count } of sources) {
    for (let depth = 1; depth <= path.length; depth++) {
      const prefix = path.slice(0, depth);
      const key = encodePath(prefix);
      const entry = counts.get(key) ?? { path: prefix, count: 0 };
      entry.count += news_count;
      counts.set(key, entry);
    }
  }
  // Uložený vzor, na ktorý už nesedí žiadna novinka, musí v ponuke zostať,
  // inak by ho `Autocomplete` nevedel zobraziť ani odobrať.
  for (const path of extra) {
    const key = encodePath(path);
    if (!counts.has(key)) counts.set(key, { path, count: 0 });
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) =>
      formatSourcePath(a.path).localeCompare(formatSourcePath(b.path), 'sk'),
    )
    .map(([value, { path, count }]) => ({
      value,
      label: `${formatSourcePath(path)} (${count})`,
    }));
};

/**
 * Koľko noviniek patrí pod vzor - súčet cez všetky zdrojové cesty, ktorých je
 * vzor prefixom. To isté pravidlo, podľa ktorého API novinky do kategórie
 * zaraďuje.
 */
export const newsCountForPattern = (
  sources: NewsSource[],
  pattern: NewsSourcePath,
) =>
  sources
    .filter(({ path }) =>
      pattern.every((segment, index) => path[index] === segment),
    )
    .reduce((sum, { news_count }) => sum + news_count, 0);
